/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import React, { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { Link, navigate, RouteComponentProps } from "@reach/router";
import css from "classnames";
import { union, values } from "lodash";
import { Grid, Icon } from "@material-ui/core";
import { Button } from "components/haven-design/buttons";
import { ContainerCard } from "components/haven-design";
import { SelectAutocomplete } from "components/haven-design/form/select";
import { userLoginToken } from "utils/general-utils";
import {
  Actions,
  Document,
  HTTPStatus,
  reducer,
} from "pages/dashboard/interests-documents/reducer";
import { validate } from "pages/dashboard/interests-documents/utils";
import { ALLOWED_FILE_TYPES, CATEGORIES } from "pages/dashboard/interests-documents/constants";
import {
  DOCUMENT_COMPLETE,
  DOCUMENT_PREFLIGHT,
} from "pages/dashboard/interests-documents/query";
import FileUploadButton from "components/haven-design/file-upload-button";
import {
  DocumentsUploadComplete,
  DocumentsUploadCompleteVariables,
  RequestDocumentPreflights,
  RequestDocumentPreflightsVariables,
} from "types/gql-types";
import styles from "./styles.module.css";

const exts = union(...ALLOWED_FILE_TYPES.map((ft) => ft.ext));


const optionToCategory = (option: string | null) =>
  CATEGORIES.find((category) => category.name === option);

// Need a global reference here due to interaction between ref callback and needing to
// trigger file upload dialogue only once by leveraging useEffect
// eslint-disable-next-line init-declarations
let uploadButtonRefNode: HTMLInputElement;

type DashboardProps = RouteComponentProps<{ location: { state: { addDocumentIndex?: number } } }>;

const Dashboard: React.FC<DashboardProps> = (props) => {
  const [preflight] = useMutation<RequestDocumentPreflights, RequestDocumentPreflightsVariables>(
    DOCUMENT_PREFLIGHT,
  );

  const [complete] = useMutation<DocumentsUploadComplete, DocumentsUploadCompleteVariables>(
    DOCUMENT_COMPLETE,
    { refetchQueries: ["UserDocuments"] },
  );

  // Local state and associated dispatcher for managing the state associated
  // with selecting documents from the local filesystem, selecting categories,
  // and any related request statuses.
  const [state, dispatch] = React.useReducer(reducer, {});
  const [fileInputKey, setFileInputKey] = React.useState(0);

  // The local selected files state modified with any potential validation
  // issues.
  const docs = values(validate(state));

  // Filter function returns only selected documents that are valid and haven't
  // been uploaded yet.
  const uploadable = (doc: Document) => doc.validation === null && doc.httpStatus === null;

  const isAnyUploading = docs.some((doc) => doc.httpStatus === HTTPStatus.Loading);
  // convert to are all uploadable
  const areAllUploadable = docs.filter(uploadable).length === docs.length;

  // onSubmitUpload manages the dance thats initiated when the selected
  // documents are submitted for upload. The following high-level requests
  // occur:
  //  1. Sends one GraphQL mutation for creating one or more document preflight
  //    requests. This mutation returns a list of the document objects, the
  //    temporary ID associated with that request, and a pre-signed S3 URL
  //    which will be used when uploading the document contents to S3.
  //  2. Once the preflight request is completed, one or more HTTP requests
  //    are started to the S3 service, each containing the contents of one of
  //    the documents.
  //  3. Once all S3 uploads are completed, the documents which were
  //    successfully uploaded are then collected and sent in one last GraphQL
  //    mutation to mark them as having been successfully uploaded.
  //  4. Finally, once the uploaded documents are marked as completed, the
  //    query to fetch all uploaded documents for the current interest is
  //    performed and the UI is updated to reflect the newly uploaded
  //    documents.
  const onSubmitUpload = () => {
    const token = userLoginToken();
    if (token === null) {
      throw new Error("Unexpected absence of the user's login token.");
    }

    // Create a set of preflight document request objects which will be passed
    // as a variable to the document preflight GraphQL mutation.
    const documents = docs.filter(uploadable).map((document) => ({
      tempId: document.id,
      filename: document.file.name,
      filesize: document.file.size,
      category: document.category as string,
      interest: null,
    }));

    // Update the local state of the selected documents as having begun their
    // upload journey.
    dispatch({
      type: Actions.StartPreflight,
      payload: { documents: documents.map((document) => document.tempId) },
    });

    // Perform the document preflight request. Once completed, individual HTTP
    // requests will be performed with the returned pre-signed S3 URLs.
    preflight({ variables: { documents } }).then((response) => {
      if (!response.data) {
        return;
      }

      const { documentPreflight } = response.data;

      const requests = documentPreflight.map(
        ({ uploadUrl, tempId, document }) =>
          new Promise<string>((resolve, reject) => {
            if (!(tempId in state)) {
              throw new Error("Unexpected response from the server.");
            }

            const params = { method: "put", body: state[tempId].file };
            fetch(uploadUrl, params).then((uploadResponse) => {
              dispatch({
                type: Actions.S3UploadResponse,
                payload: { id: tempId, response: uploadResponse },
              });

              if (uploadResponse.status >= 200 && uploadResponse.status < 300) {
                resolve(document.id);
                return;
              }

              reject(document.id);
            });
          }),
      );

      Promise.all(requests).then((uploadedDocuments) => {
        complete({ variables: { documents: uploadedDocuments } });
        navigate("/dash/client/documents");
      });
    });
  };

  const onFileInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;

    dispatch({
      type: Actions.SelectFiles,
      payload: {
        files: values(target.files),
      },
    });
  };

  // used to keep track of whether we've added a document when the "Add document" button
  // is clicked elsewhere
  const [readyForUploadClick, setReadyForUploadClick] = useState(false);

  const uploadButtonRef = (node: HTMLInputElement) => {
    if (node) {
      // Manually setting onchange here is necessary due to some sort of low-level React bug where,
      // upon programatically clicking the uploadButtonRefNode, it immediately destroys
      // and recreates the element. During this destruction, the onChange handler specified later
      // in the render does not fire. Due to this, any file selected by the dialogue
      // will not fire the correct onChange handler and won't be prepped for upload.
      // By manually setting this here, we correctly trigger the onChange handler and are able
      // to work with the file again.
      // We have to ignore this due to the difference in the signature between
      // HTMLInputEventHandler onchange and React's synthetic event handlers.
      // @ts-ignore
      node.onchange = onFileInputChange;
      uploadButtonRefNode = node;
      setReadyForUploadClick(true);
    }

    return node;
  };

  const addDocumentIndex = (props.location && props.location.state?.addDocumentIndex) || -1;

  useEffect(
    () => {
      if (readyForUploadClick && addDocumentIndex !== -1) {
        uploadButtonRefNode.click();

        // cleanup nav state so refresh doesn't cause duplicate upload submissions
        if (props.navigate && props.location) {
          props.navigate(props.location.pathname, { state: {} });
        }
      }
    },
    // Only fire this effect when it's both ready for upload click (the ref we click is not null)
    // and addDocumentIndex has changed.
    [readyForUploadClick, addDocumentIndex],
  );

  return (
    <ContainerCard className={styles.container}>
      <h1>
        <Link to="/dash/client/documents">My Documents</Link> &gt; Upload Documents
      </h1>
      <p>We accept {exts.join(", ")} file types.</p>

      <FileUploadButton
        id="upload"
        name="upload"
        ref={uploadButtonRef}
        accept={exts.join(", ")}
        multiple
        onChange={onFileInputChange}
      />

      <Grid container spacing={1}>
        {docs.map((document) => (
          <Grid item xs={12} key={document.id}>
            <Grid item xs={12} sm={8} md={5}
              container
              className={css({}, {
                success: document.httpStatus === HTTPStatus.Success,
                danger: document.httpStatus === HTTPStatus.Error,
                [styles.shimmer]: document.httpStatus === HTTPStatus.Loading,
              })}
            >
              <Grid item xs={11} sm={11} className={styles.fileRowLeft}>
                <div className={styles.filename} title={document.file.name}>
                  {document.file.name}
                </div>

                <SelectAutocomplete
                  margin="dense"
                  label="Select a Category"
                  disableClearable
                  disabled={document.httpStatus !== null}
                  options={CATEGORIES}
                  getOptionLabel={(option) => option.name}
                  groupBy={({ group }) => group}
                  value={optionToCategory(document.category)}
                  data-cy="select-category"
                  onChange={(_, value) => {
                    if (value) {
                      dispatch({
                        type: Actions.ChangeFileCategory,
                        payload: { id: document.id, category: value.name },
                      });
                    }
                  }}
                />

                <div>
                  {document.httpStatus === HTTPStatus.Success && (
                    <div className="label label-success">Uploaded</div>
                  )}
                  {document.validation !== null && (
                    <div className="label label-danger">{document.validation}</div>
                  )}
                </div>
              </Grid>
              <Grid item xs={1} sm={1} className={styles.fileRowRight}>
                {document.httpStatus === null && (
                  <button
                    type="button"
                    className="close"
                    aria-label="Close"
                    onClick={() => {
                      dispatch({
                        type: Actions.RemoveFile,
                        payload: { id: document.id },
                      });
                      if (Object.keys(state).length === 1) {
                        setFileInputKey(fileInputKey + 1);
                      }
                    }}
                  >
                    <span aria-hidden="true">
                      <Icon>delete</Icon>
                    </span>
                  </button>
                )}
              </Grid>
            </Grid>
          </Grid>
        ))}

      </Grid>

      {docs.length ? (
        <div style={{ marginTop: 30 }}>
          <Button
            size="S"
            color="secondary"
            marginRight
            disabled={docs.length === 0 || isAnyUploading}
            onClick={() => {
              const msg = "You have not sent your files. Are you sure you want to exit?";
              // eslint-disable-next-line no-alert
              if (confirm(msg)) {
                dispatch({ type: Actions.ResetSelection, payload: {} });
                setFileInputKey(fileInputKey + 1);
                navigate("/dash/client/documents");
              }
            }}
          >
            Cancel
          </Button>
          <Button size="S" color="primary" onClick={onSubmitUpload} disabled={!areAllUploadable}>
            Submit uploaded files
          </Button>
        </div>
      ) : (
        <div className="emptyContainer" style={{ height: 200 }} />
      )}
    </ContainerCard>
  );
};

export default Dashboard;
